import useSWR from 'swr'
import { PowerBIEmbed } from 'powerbi-client-react'
import { useTranslation } from 'react-i18next'

import Header from './Header'

import { ReportResponse, StatsResponse } from './types'
import { useLocation } from 'react-router-dom'

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

interface ExportInitiatedResponse {
  data: {
    reportId: string
    exportId: string
  }
  status: string
  statusText: string
}

interface IsExportReadyResponse {
  data: {
    status: number
  }
  status: string
  statusText: string
}

export const ReportView = () => {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  let locale = ''
  if (language === 'fi') {
    locale = 'fi-FI'
  } else if (language === 'sv') {
    locale = 'sv-SE'
  } else if (language === 'en') {
    locale = 'en-US'
  }

  let reportParam = 'Hanketiedot'

  let embedSuffix = ''
  let backendSuffix = `?language=${language}`
  let pageType: 'searchPage' | 'projectDescription' | 'summary' = 'searchPage'
  let projectCode = ''

  if (typeof window !== 'undefined') {
    const queryParams = window.location.search
    const regex = /koodi=([^&]+)/
    const res = regex.exec(queryParams)

    if (Array.isArray(res) && res.length > 1) {
      backendSuffix = `?${res[0]}&language=${language}`
      embedSuffix = `&filter=hankehakemus/koodi%20eq%20%27${res[1]}%27`
      pageType = 'projectDescription'
      projectCode = res[1]
    }

    const locationRegexRes = /\/([a-zA-Z0-9_]+)/.exec(window.location.pathname)
    if (Array.isArray(locationRegexRes) && locationRegexRes.length > 1 && locationRegexRes[1] === 'ohjelmatulokset') {
      reportParam = `report=Ohjelmatulokset_${language}`
      pageType = 'summary'
    }
  }

  /**
   * These params are appended to embedUrl to modify report content
   * - Disable sheetbar:    &navContentPaneEnabled=false
   * - Pass report params:  &rp:param_name=value
   */
  const embedParams = `&navContentPaneEnabled=false${embedSuffix}` //"&rp:viranomainen=0000";
  const backendURL = `/reporting/tiepa/api/reports${backendSuffix}&${reportParam}`

  const { data: resp, error: err } = useSWR<ReportResponse>(backendURL, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0
  })
  const { data: stats } = useSWR<StatsResponse>('/tiepa/stats')

  let updatedAtDate: null | Date = null
  let updatedAt: null | string = ''
  if (stats && stats.hasOwnProperty('luontiaika')) {
    updatedAtDate = new Date(stats.luontiaika)
    if (!isNaN(updatedAtDate.getTime())) {
      updatedAt = updatedAtDate.toLocaleDateString('fi-FI')
    }
  }

  return (
    <div id="container">
      <Header />
      <div id="content">
        <div id="bread-crumb">
          <a href="/tiepa">{t('frontPage')}</a> »{' '}
          {pageType === 'searchPage' ? (
            t('projectInfoService')
          ) : null}
          {pageType === 'projectDescription' ? (
            <>
              <a href="/hanketietopalvelu">{t('projectInfoService')}</a> » {t('project')} {projectCode}
            </>
          ) : null}
          {pageType === 'summary' ? (
            t('tiepaResultsLong')
          ) : null}
        </div>
        {updatedAt ? <div id="updated-at">{t('updatedAt', { date: updatedAt })}</div> : null}
        {pageType === 'searchPage' ? <div id="project-info-description">{t('projectInfoDescription')}</div> : null}
        {err ? (
          <div className="loading-info-container">{`${t('loadingFailed')}:\n${err}`}</div>
        ) : !resp ? (
          <div className="loading-info-container">{t('loadingReport')}...</div>
        ) : resp.capacity === 'pending' ? (
          <div className="loading-info-container">{t('loadingCapacity')}...</div>
        ) : resp.capacity === 'restarting' ? (
          <div className="loading-info-container">{t('startingCapacity')}...</div>
        ) : (
          <>
            {resp.hasOwnProperty('reportGroups') ? (
              <>
                {resp.reportGroups.map((report) => (
                  <PowerBIEmbed
                    key={language}
                    embedConfig={{
                      accessToken: resp.token,
                      embedUrl: report.embedUrl + embedParams,
                      id: report.id,
                      tokenType: 1,
                      type: 'report',
                      settings: {
                        localeSettings: {
                          language: language,
                          formatLocale: locale,
                        },
                        commands: [
                          {
                            drill: { displayOption: 2 },
                            drillthrough: { displayOption: 2 },
                            expandCollapse: { displayOption: 2 },
                            includeExclude: { displayOption: 2 },
                            removeVisual: { displayOption: 2 },
                            spotlight: { displayOption: 2 },
                            seeData: { displayOption: 2 },
                          },
                        ],
                      },
                    }}
                    eventHandlers={
                      new Map([
                        ['loaded', () => console.log('Report loaded')],
                        ['rendered', () => console.log('Report rendered')],
                        ['error', (err: any) => console.log(`Error loading report:\n${err}`)],
                      ])
                    }
                    cssClassName="report"
                  />
                ))}
              </>
            ) : (
              <div className="loading-info-container">{t('loadingFailed')}</div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
